import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const Stars = ({ rating = 0 }) => (
  <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={15}
        strokeWidth={0}
        className={n <= Math.round(rating) ? "fill-[#E60000]" : "fill-white/15"}
      />
    ))}
  </div>
);

export const Reviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ enabled: false, reviews: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data || !data.enabled || !data.reviews?.length) return null;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative overflow-hidden border-t border-white/10 bg-[#050505] py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]"
            >
              <span className="h-px w-10 bg-[#E60000]" />
              Google reviews
            </motion.p>
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl"
            >
              Real cars.
              <br />
              <span className="text-outline-red">Real results.</span>
            </motion.h2>
          </div>

          {data.rating != null && (
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              data-testid="reviews-summary"
              className="flex items-center gap-4 border border-white/10 bg-white/[0.03] px-6 py-4"
            >
              <span className="font-display text-4xl font-bold text-white">
                {Number(data.rating).toFixed(1)}
              </span>
              <div>
                <Stars rating={data.rating} />
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-500">
                  {data.user_rating_count ?? 0} reviews
                </p>
              </div>
            </motion.div>
          )}
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {data.reviews.map((r, i) => (
            <motion.article
              key={r.google_maps_uri ?? i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: (i % 3) * 0.08 }}
              data-testid={`review-card-${i}`}
              className="flex flex-col border border-white/10 bg-[#0A0A0A] p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#E60000]/50"
            >
              <Stars rating={r.rating} />
              <p className="mt-5 flex-1 text-sm leading-relaxed text-zinc-300 line-clamp-6">
                “{r.text}”
              </p>
              <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                {r.author_photo ? (
                  <img
                    src={r.author_photo}
                    alt={r.author_name || "Google reviewer"}
                    className="h-9 w-9 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E60000]/15 font-display text-sm font-bold text-[#E60000]">
                    {(r.author_name || "G").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <a
                    href={r.author_uri || r.google_maps_uri}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate font-display text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:text-[#E60000]"
                  >
                    {r.author_name || "Google reviewer"}
                  </a>
                  {r.relative_time && (
                    <span className="text-xs text-zinc-500">{r.relative_time}</span>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <p className="mt-8 text-xs text-zinc-500">
          Shown by relevance.{" "}
          <a
            href={data.google_maps_uri}
            target="_blank"
            rel="noreferrer"
            data-testid="reviews-see-all-link"
            className="inline-flex items-center gap-1 font-semibold text-zinc-300 underline decoration-[#E60000] decoration-2 underline-offset-4 transition-colors hover:text-white"
          >
            See all reviews on Google
            <ArrowUpRight size={13} strokeWidth={2} />
          </a>
        </p>
      </div>
    </section>
  );
};

export default Reviews;
